// src/components/Admin/ReportManager/ReportCardItem.jsx
import React, { useMemo } from 'react';
import {
    Paper,
    Box,
    Stack,
    Typography,
    Chip,
    Button,
    Divider,
} from '@mui/material';
import dayjs from 'dayjs';

const STATUS_LABEL = {
    pending: '대기',
    blinded: '블라인드',
    deleted: '삭제',
    ignored: '무시',
};

const statusColor = (status) => {
    if (status === 'pending') return 'default';
    if (status === 'blinded') return 'warning';
    if (status === 'deleted') return 'error';
    if (status === 'ignored') return 'info';
    return 'default';
};

export default function ReportCardItem({
    report,
    onProcess,
    onLogView,
    onShowReporters,
}) {
    const typeLabel = report.type_label || report.type || report.target_type || '구분';
    const title = report.target_title || report.title || report.target || '신고 대상';
    const reason = report.reason || '-';
    const reporter = report.reporter || '-';
    const count = report.report_count ?? report.count ?? 0;
    const status = report.status || 'pending';
    const processor = report.processor || report.processed_by || '-';
    const createdAt = report.created_at ? dayjs(report.created_at) : null;

    const dateStr = useMemo(() => {
        if (!createdAt) return '-';
        return createdAt.format('YYYY-MM-DD');
    }, [createdAt]);

    const timeStr = useMemo(() => {
        if (!createdAt) return '';
        return createdAt.format('A hh:mm');
    }, [createdAt]);

    // ✅ onProcess 시그니처 흡수용: (id, action) / (id, action, report) 모두 대응
    const fireProcess = (action) => {
        if (!onProcess) return;
        onProcess(report.id, action, report);
    };

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 1.5,
                borderRadius: 2,
                width: '100%',
                minWidth: 0,
            }}
        >
            {/* 상단: 구분 / 상태 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Chip size="small" variant="outlined" label={typeLabel} sx={{ flex: 'none' }} />
                <Chip
                    size="small"
                    color={statusColor(status)}
                    label={STATUS_LABEL[status] || status}
                    sx={{ flex: 'none' }}
                />
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                    {dateStr} {timeStr}
                </Typography>
            </Box>

            {/* 대상 */}
            <Typography
                sx={{
                    mt: 1,
                    fontWeight: 800,
                    fontSize: '0.95rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
                title={title}
            >
                {title}
            </Typography>

            {/* 사유 */}
            <Typography sx={{ mt: 0.75, fontSize: '0.85rem' }}>
                <b>사유</b>: {reason}
            </Typography>

            {/* 메타 */}
            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip size="small" variant="outlined" label={`신고자: ${reporter}`} />
                <Chip size="small" variant="outlined" label={`횟수: ${count}`} />
                <Chip size="small" variant="outlined" label={`처리자: ${processor}`} />
            </Box>

            <Divider sx={{ my: 1.25 }} />

            {/* 액션 */}
            <Stack spacing={1}>
                <Stack direction="row" spacing={1}>
                    <Button
                        size="small"
                        variant="outlined"
                        fullWidth
                        onClick={() => onShowReporters?.(report.type || report.target_type || 'unknown', report.id)}
                    >
                        신고자 보기
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        fullWidth
                        onClick={() => onLogView?.(report.id)}
                    >
                        로그 보기
                    </Button>
                </Stack>

                {/* 처리 버튼들 (프로젝트 정책에 맞게 action 문자열만 맞추면 됨) */}
                {status === 'pending' && (
                    <Stack direction="row" spacing={1}>
                        <Button size="small" variant="contained" color="warning" fullWidth onClick={() => fireProcess('blind')}>
                            블라인드
                        </Button>
                        <Button size="small" variant="contained" color="error" fullWidth onClick={() => fireProcess('delete')}>
                            삭제
                        </Button>
                        <Button size="small" variant="outlined" fullWidth onClick={() => fireProcess('ignore')}>
                            무시
                        </Button>
                    </Stack>
                )}

                {status === 'blinded' && (
                    <Stack direction="row" spacing={1}>
                        <Button size="small" variant="contained" color="primary" fullWidth onClick={() => fireProcess('unblind')}>
                            블라인드 해제
                        </Button>
                        <Button size="small" variant="outlined" fullWidth onClick={() => fireProcess('ignore')}>
                            무시
                        </Button>
                    </Stack>
                )}

                {status === 'deleted' && (
                    <Stack direction="row" spacing={1}>
                        <Button size="small" variant="contained" color="primary" fullWidth onClick={() => fireProcess('restore')}>
                            복구
                        </Button>
                        <Button size="small" variant="outlined" fullWidth onClick={() => fireProcess('ignore')}>
                            무시
                        </Button>
                    </Stack>
                )}

                {status === 'ignored' && (
                    <Stack direction="row" spacing={1}>
                        <Button size="small" variant="outlined" fullWidth onClick={() => fireProcess('reopen')}>
                            다시 대기
                        </Button>
                        <Button size="small" variant="contained" color="warning" fullWidth onClick={() => fireProcess('blind')}>
                            블라인드
                        </Button>
                    </Stack>
                )}
            </Stack>
        </Paper>
    );
}
